import {
  useEffect,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

/**
 * Closes the popup menu on Escape and returns focus to its trigger.
 * Outside presses are handled by `PopupMenuOverlay`.
 */
export function usePopupMenuDismissal({
  activeTriggerRef,
  isOpen,
  setIsOpen,
  triggerRef,
}: {
  activeTriggerRef?: RefObject<HTMLElement | null>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  triggerRef: RefObject<HTMLButtonElement>;
}) {
  const restoreFocusToTrigger = () => {
    const focusTarget = activeTriggerRef?.current || triggerRef.current;
    focusTarget?.focus();
  };

  const closeMenu = () => {
    setIsOpen(false);
    restoreFocusToTrigger();
  };

  useEffect(() => {
    if (!isOpen) return;

    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      restoreFocusToTrigger();
    };

    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [activeTriggerRef, isOpen, setIsOpen, triggerRef]);

  return {
    closeMenu,
  };
}
